//frontend/src/components/client/HeroSection.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaBoxOpen, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import axiosInstance from '../../axios';

const HeroSection = () => {
  const [trackingCode, setTrackingCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [banners, setBanners] = useState([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const intervalRef = useRef(null); // ⭐ Interval-г хянах
  const navigate = useNavigate();

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const response = await axiosInstance.get('/api/hero-banners/active');
      // console.log('🔍 Banner Response:', response.data);
      
      if (response.data.success && response.data.data && response.data.data.length > 0) {
        // console.log('✅ Banners loaded:', response.data.data);
        setBanners(response.data.data);
      } else {
        // console.log('⚠️ No banners, using default');
        setBanners([getDefaultBanner()]);
      }
    } catch (error) {
      // console.error('❌ Fetch banners error:', error);
      setBanners([getDefaultBanner()]);
    }
  };

  const getDefaultBanner = () => ({
    id: 0,
    title: '',
    subtitle: '',
    description: '',
    button_text: 'Ачаа хайх',
    background_color: '#4f46e5',
    transition_duration: 5000
  });

  // ⭐ Дараагийн баннер руу шилжих функц - dependency issues засах
  const goToNextSlide = useCallback(() => {
    setIsTransitioning(true);
    setCurrentBannerIndex((prev) => {
      const nextIndex = (prev + 1) % banners.length;
      // console.log(`🔄 Баннер солигдож байна: ${prev} → ${nextIndex}`);
      return nextIndex;
    });
    
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  }, [banners.length]); // ⭐ isTransitioning хасах

  // ⭐ Автоматаар баннер солих - interval засах
  useEffect(() => {
    // 1 баннер буюу хоосон бол carousel ажиллуулахгүй
    if (banners.length <= 1) {
      // console.log('⏸️ 1 баннер байна, carousel идэвхгүй');
      return;
    }

    // Өмнөх interval-г цэвэрлэх
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Одоогийн баннерийн transition duration авах
    const currentBanner = banners[currentBannerIndex];
    const duration = currentBanner?.transition_duration || 5000;
    
    // console.log(`⏱️ Баннер ${currentBannerIndex + 1}: ${duration}ms хүлээнэ`);

    // Шинэ interval эхлүүлэх
    intervalRef.current = setInterval(() => {
      goToNextSlide();
    }, duration);

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [banners, currentBannerIndex, goToNextSlide]); // ⭐ Dependencies бүрэн

  // Гараар өмнөх баннер руу шилжих
  const goToPrevBanner = () => {
    if (isTransitioning || banners.length <= 1) return;
    
    // Interval-г дахин эхлүүлэхийн тулд index өөрчлөх
    setCurrentBannerIndex((prev) => {
      const prevIndex = (prev - 1 + banners.length) % banners.length;
      // console.log(`◀️ Өмнөх баннер: ${prev} → ${prevIndex}`);
      return prevIndex;
    });
  };

  // Гараар дараагийн баннер руу шилжих
  const goToNextBanner = () => {
    if (isTransitioning || banners.length <= 1) return;
    
    // goToNextSlide дуудах - interval дахин эхэлнэ
    goToNextSlide();
  };

  // Тодорхой баннер руу шууд очих
  const goToBanner = (index) => {
    if (isTransitioning || index === currentBannerIndex || banners.length <= 1) return;
    
    setIsTransitioning(true);
    setCurrentBannerIndex(index);
    // console.log(`🎯 Баннер ${index + 1} руу очлоо`);
    
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  };

  const handleTrackingSubmit = (e) => {
    e.preventDefault();
    if (!trackingCode.trim()) {
      alert('Ачааны код оруулна уу!');
      return;
    }
    
    setShowTrackingModal(false);
    navigate(`/tracking/${trackingCode.trim()}`);
  };

  const currentBanner = banners[currentBannerIndex] || getDefaultBanner();
  
  // console.log('🎨 Current Banner:', currentBanner);
  // console.log('🖼️ Background Image URL:', currentBanner.background_image);
  // console.log('⏱️ Transition Duration:', currentBanner.transition_duration);

  const heroStyle = {
    backgroundImage: currentBanner.background_image ? `url("${currentBanner.background_image}")` : 'none',
    backgroundColor: currentBanner.background_color || '#4f46e5',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  };

  return (
    <>
      <section className="hero-section" style={heroStyle}>
        <div className="hero-overlay"></div>
        
        <div className={`hero-content ${isTransitioning ? 'hero-transitioning' : ''}`}>
          <div className="hero-text">
            <h1 className="hero-title">
              <span className="hero-title-main">
                {currentBanner.title || ''}
              </span>
              <span className="hero-title-sub">
                {currentBanner.subtitle || ''}
              </span>
            </h1>
            <p className="hero-description">
              {currentBanner.description || ''}
            </p>
          </div>
        </div>

        {/* TRACKING BUTTON - Баруун доод булан */}
        <div className="tracking-button-container">
          <button
            onClick={() => setShowTrackingModal(true)}
            className="tracking-open-btn"
          >
            <FaSearch className="tracking-btn-icon" />
            <span className="tracking-btn-text">
              {currentBanner.button_text || 'Ачаа хайх'}
            </span>
          </button>
        </div>

        {/* Carousel Navigation Arrows */}
        {banners.length > 1 && (
          <>
            <button 
              onClick={goToPrevBanner} 
              className="carousel-arrow carousel-arrow-left"
              disabled={isTransitioning}
              aria-label="Өмнөх баннер"
            >
              <FaChevronLeft />
            </button>
            <button 
              onClick={goToNextBanner} 
              className="carousel-arrow carousel-arrow-right"
              disabled={isTransitioning}
              aria-label="Дараагийн баннер"
            >
              <FaChevronRight />
            </button>
          </>
        )}

        {/* Carousel Indicators */}
        {banners.length > 1 && (
          <div className="carousel-indicators">
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={() => goToBanner(index)}
                className={`carousel-dot ${index === currentBannerIndex ? 'active' : ''}`}
                disabled={isTransitioning}
                aria-label={`Баннер ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* WAVE DECORATION */}
        <div className="hero-wave">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 0L60 10C120 20 240 40 360 46.7C480 53 600 47 720 43.3C840 40 960 40 1080 46.7C1200 53 1320 67 1380 73.3L1440 80V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* TRACKING MODAL */}
      {showTrackingModal && (
        <div className="tracking-modal-overlay" onClick={() => setShowTrackingModal(false)}>
          <div className="tracking-modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowTrackingModal(false)}
              className="tracking-modal-close"
            >
              <FaTimes />
            </button>
            
            <h2 className="tracking-modal-title">
              <FaBoxOpen className="inline mr-2" />
              Ачаа хайх
            </h2>
            
            <form onSubmit={handleTrackingSubmit} className="tracking-modal-form">
              <div className="tracking-input-group">
                <FaSearch className="tracking-input-icon" />
                <input
                  type="text"
                  placeholder="Ачааны код оруулна уу..."
                  value={trackingCode}
                  onChange={(e) => setTrackingCode(e.target.value)}
                  className="tracking-input"
                  disabled={loading}
                  autoFocus
                />
              </div>
              <button
                type="submit"
                className="tracking-submit-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="btn-spinner"></div>
                    <span>Хайж байна...</span>
                  </>
                ) : (
                  <>
                    <FaSearch className="mr-2" />
                    <span>Хайх</span>
                  </>
                )}
              </button>
            </form>
            
            <p className="tracking-hint">
              Ачааны код нь баримт дээрх дугаар эсвэл SMS-ээр ирсэн код байна
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default HeroSection;